import React, { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate, useNavigate } from "react-router-dom";
import { api, formatError, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtMoney, fmtDateShort, randToCents } from "@/lib/format";
import { PaginationBar } from "@/components/PaginationBar";
import { toast } from "sonner";
import RatesDefaultPage from "./finance/RatesDefaultPage";
import UninvoicedChargesPage from "./finance/UninvoicedChargesPage";
import ArSummaryStrip from "./finance/ArSummaryStrip";

/** Umbrella Finance module for truck-stop owner/admin. Nav-gated by tenant.feature_flags.financial. */
export default function FinanceModule() {
  const { tenant } = useAuth();
  if (!tenant?.feature_flags?.financial) {
    return (
      <div className="bg-white border border-slate-200 rounded p-8 text-center">
        <div className="label-overline">Finance disabled</div>
        <p className="mt-2 text-slate-600">Finance is not enabled for this account. Ask the platform owner to upgrade the plan.</p>
      </div>
    );
  }
  return (
    <Routes>
      <Route index element={<FinanceDashboard />} />
      <Route path="opening" element={<OpeningBalances />} />
      <Route path="rates" element={<RatesDefaultPage />} />
      <Route path="uninvoiced-charges" element={<UninvoicedChargesPage />} />
      <Route path="invoices" element={<Invoices />} />
      <Route path="credit-notes" element={<CreditNotes />} />
      <Route path="payments" element={<Payments />} />
      <Route path="statements" element={<Statements />} />
      <Route path="branding" element={<Branding />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}

const dl = (p) => fetch(`${API_BASE}${p}`, { headers: { Authorization: `Bearer ${getToken()}` } })
  .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"));

function FinanceDashboard() {
  const [balances, setBalances] = useState([]);
  useEffect(() => { api.get("/finance/reports/balances").then(({ data }) => setBalances(data.items)); }, []);
  const total = balances.reduce((a, b) => a + (b.balance_cents || 0), 0);
  return (
    <div>
      <div className="label-overline">Finance</div>
      <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">Finance Dashboard</h1>
      <div className="mt-6">
        <ArSummaryStrip />
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          ["opening", "Opening Balances"], ["invoices", "Invoices"],
          ["credit-notes", "Credit Notes"], ["payments", "Payments"],
          ["statements", "Statements"], ["branding", "Branding"],
        ].map(([p, l]) => (
          <Link key={p} to={p} className="card-tile text-center font-semibold text-blue-700" data-testid={`fin-nav-${p}`}>{l}</Link>
        ))}
      </div>
      <div className="mt-6 bg-white border border-slate-200 rounded overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-200 label-overline">Customer balances</div>
        <table className="tbl w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50"><tr className="label-overline text-slate-600">
            <th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Customer</th>
            <th className="text-right px-4 py-2">Balance</th><th className="text-right px-4 py-2">Credit limit</th>
          </tr></thead>
          <tbody>
            {balances.map((b) => (
              <tr key={b.id} className="border-b border-slate-100" data-testid={`bal-row-${b.customer_code}`}>
                <td className="px-4 py-2 font-mono">{b.customer_code}</td>
                <td className="px-4 py-2 font-semibold">{b.display_name}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(b.balance_cents)}</td>
                <td className="px-4 py-2 text-right font-mono text-slate-500">{b.credit_limit_cents ? fmtMoney(b.credit_limit_cents) : "—"}</td>
              </tr>
            ))}
            {balances.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No balances yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Stat = ({ label, value, tone = "default" }) => {
  const toneCls = { default: "text-slate-900", blue: "text-blue-700", warn: "text-amber-700" }[tone];
  return (
    <div className="bg-white border border-slate-200 rounded p-5">
      <div className="label-overline">{label}</div>
      <div className={`mt-2 text-3xl font-black font-mono ${toneCls}`}>{value}</div>
    </div>
  );
};

function OpeningBalances() {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/finance/opening-balances").then(({ data }) => setSt(data)); }, []);
  const setMethod = async (method) => {
    try { const { data } = await api.post("/finance/opening-balances/method", { method }); setSt(data); }
    catch (e) { toast.error(formatError(e, "Failed")); }
  };
  const lock = async () => {
    try { const { data } = await api.post("/finance/opening-balances/lock"); setSt(data); toast.success("Opening balances locked"); }
    catch (e) { toast.error(formatError(e, "Failed")); }
  };
  if (!st) return <div className="label-overline text-slate-500">Loading…</div>;
  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-black tracking-tight text-slate-900">Opening Balances</h1>
      <div className="mt-2 text-sm text-slate-600">State: <b>{st.state}</b> · Method: <b>{st.method || "—"}</b>{st.locked_at && ` · Locked ${st.locked_at.substring(0, 10)}`}</div>
      {st.state !== "locked" && (
        <>
          <div className="mt-4 label-overline">Choose method</div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {["zero", "manual", "import"].map((m) => (
              <button key={m} onClick={() => setMethod(m)} data-testid={`ob-method-${m}`}
                      className={`border rounded py-3 font-semibold ${st.method === m ? "border-blue-700 bg-blue-50 text-blue-800" : "border-slate-300"}`}>
                {m === "zero" ? "Zero (R0 for all)" : m === "manual" ? "Manual entry" : "Import (later)"}
              </button>
            ))}
          </div>
          {st.method && (
            <Button data-testid="ob-lock" onClick={lock}
                    className="mt-4 rounded bg-blue-700 hover:bg-blue-800">Lock opening balances</Button>
          )}
        </>
      )}
      {st.state === "locked" && (
        <div className="mt-4 p-4 bg-green-50 border border-green-300 rounded">
          Locked. {st.entry_count || 0} ledger entries created. Contact the platform owner to reset.
        </div>
      )}
    </div>
  );
}

function CustomerPicker({ value, onChange, testId }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!q || q.length < 2) return setOpts([]);
    const t = setTimeout(() => {
      api.get("/customers", { params: { q, limit: 8 } }).then(({ data }) => setOpts(data.items));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  if (value) return (
    <div className="flex items-center gap-2 bg-blue-50 border border-blue-600 rounded p-2">
      <div className="font-mono text-xs text-slate-500">{value.customer_code}</div>
      <div className="font-semibold flex-1">{value.display_name}</div>
      <button type="button" onClick={() => onChange(null)} className="text-blue-700 text-xs underline">Change</button>
    </div>
  );
  return (
    <div className="relative">
      <Input data-testid={testId} placeholder="Search customer…" className="rounded h-10"
             value={q} onChange={(e) => setQ(e.target.value)} />
      {opts.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded shadow max-h-56 overflow-y-auto">
          {opts.map((c) => (
            <button key={c.id} type="button" onClick={() => { onChange(c); setQ(""); setOpts([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0">
              <div className="font-mono text-xs text-slate-500">{c.customer_code}</div>
              <div className="font-semibold">{c.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Invoices() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [creating, setCreating] = useState(false);
  const [cust, setCust] = useState(null);
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("");

  const load = () => api.get("/finance/invoices", { params: { page, limit: 20 } })
    .then(({ data }) => { setItems(data.items); setPages(data.pages); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const create = async () => {
    if (!cust) return toast.error("Pick a customer");
    const q = parseFloat(qty) || 1;
    const u = randToCents(unit);
    const total = Math.round(q * u);
    try {
      const { data } = await api.post("/finance/invoices", {
        customer_account_id: cust.id,
        lines: [{ description: desc || "Charge", quantity: q, unit_price_cents: u, line_total_cents: total }],
      });
      await api.post(`/finance/invoices/${data.id}/issue`);
      toast.success("Invoice issued");
      setCreating(false); setCust(null); setDesc(""); setQty("1"); setUnit(""); load();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-4 flex-wrap gap-2">
        <h1 className="text-3xl font-black tracking-tight text-slate-900">Invoices</h1>
        <Button className="rounded bg-blue-700 hover:bg-blue-800" data-testid="inv-new" onClick={() => setCreating(true)}>+ New invoice</Button>
      </div>
      {creating && (
        <div className="bg-white border border-slate-200 rounded p-4 mb-4 space-y-3">
          <Label className="label-overline">Customer</Label>
          <CustomerPicker value={cust} onChange={setCust} testId="inv-customer" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="md:col-span-2"><Label className="label-overline">Description</Label>
              <Input data-testid="inv-desc" className="rounded h-10 mt-1" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
            <div><Label className="label-overline">Qty</Label>
              <Input data-testid="inv-qty" className="rounded h-10 mt-1 font-mono" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
            <div><Label className="label-overline">Unit price (R)</Label>
              <Input data-testid="inv-unit" className="rounded h-10 mt-1 font-mono" placeholder="150.00" value={unit} onChange={(e) => setUnit(e.target.value)} /></div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="rounded" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="rounded bg-blue-700 hover:bg-blue-800" data-testid="inv-issue" onClick={create}>Create & issue</Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50"><tr className="label-overline text-slate-600">
            <th className="text-left px-4 py-2">Invoice #</th><th className="text-left px-4 py-2">Customer</th>
            <th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Total</th>
            <th className="text-left px-4 py-2">Issued</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id} className="border-b border-slate-100">
                <td className="px-4 py-2 font-mono">{i.invoice_no || "DRAFT"}</td>
                <td className="px-4 py-2">{i.customer_snapshot?.display_name}</td>
                <td className="px-4 py-2 font-mono">{i.status}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(i.total_cents)}</td>
                <td className="px-4 py-2 text-xs font-mono">{i.issued_at ? fmtDateShort(i.issued_at) : "—"}</td>
                <td className="px-4 py-2"><button className="text-blue-700 font-semibold" onClick={() => dl(`/finance/invoices/${i.id}/pdf`)}>PDF ↓</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No invoices yet</td></tr>}
          </tbody>
        </table>
        <PaginationBar page={page} pages={pages} onChange={setPage} />
      </div>
    </div>
  );
}

function CreditNotes() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/finance/credit-notes").then(({ data }) => setItems(data.items)); }, []);
  return (
    <div>
      <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-4">Credit Notes</h1>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[600px]">
          <thead className="bg-slate-50"><tr className="label-overline text-slate-600">
            <th className="text-left px-4 py-2">CN #</th><th className="text-left px-4 py-2">Customer</th>
            <th className="text-left px-4 py-2">Status</th><th className="text-right px-4 py-2">Total</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-slate-100">
                <td className="px-4 py-2 font-mono">{c.credit_note_no || "DRAFT"}</td>
                <td className="px-4 py-2">{c.customer_snapshot?.display_name}</td>
                <td className="px-4 py-2 font-mono">{c.status}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(c.total_cents)}</td>
                <td className="px-4 py-2"><button className="text-blue-700 font-semibold" onClick={() => dl(`/finance/credit-notes/${c.id}/pdf`)}>PDF ↓</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No credit notes yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Payments() {
  const [items, setItems] = useState([]);
  const [cust, setCust] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("eft");
  const [ref, setRef] = useState("");
  const load = () => api.get("/finance/payments").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!cust) return toast.error("Pick a customer");
    const cents = randToCents(amount);
    if (cents <= 0) return toast.error("Enter an amount");
    try {
      await api.post("/finance/payments", { customer_account_id: cust.id, method, amount_cents: cents, reference: ref });
      toast.success("Payment recorded"); setAmount(""); setRef(""); setCust(null); load();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };
  return (
    <div>
      <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-4">Payments Received</h1>
      <div className="bg-white border border-slate-200 rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div className="md:col-span-2"><Label className="label-overline">Customer</Label>
          <CustomerPicker value={cust} onChange={setCust} testId="pay-customer" /></div>
        <div><Label className="label-overline">Method</Label>
          <select className="w-full h-10 mt-1 border border-slate-300 rounded px-2 font-mono" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="eft">eft</option><option value="cash">cash</option><option value="card">card</option><option value="other">other</option>
          </select></div>
        <div><Label className="label-overline">Amount (R)</Label>
          <Input data-testid="pay-amount" className="rounded h-10 mt-1 font-mono" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
        <div><Label className="label-overline">Reference</Label>
          <Input data-testid="pay-ref" className="rounded h-10 mt-1 font-mono" value={ref} onChange={(e) => setRef(e.target.value)} /></div>
        <Button className="rounded bg-blue-700 hover:bg-blue-800" data-testid="pay-submit" onClick={submit}>Record payment</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[600px]">
          <thead className="bg-slate-50"><tr className="label-overline text-slate-600">
            <th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Customer</th>
            <th className="text-left px-4 py-2">Method</th><th className="text-right px-4 py-2">Amount</th>
            <th className="text-left px-4 py-2">Reference</th>
          </tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-slate-100">
                <td className="px-4 py-2 font-mono text-xs">{p.received_date}</td>
                <td className="px-4 py-2">{p.customer_snapshot?.display_name}</td>
                <td className="px-4 py-2 font-mono">{p.method}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(p.amount_cents)}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.reference || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-500">No payments yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Statements() {
  const [items, setItems] = useState([]);
  const [cust, setCust] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const load = () => api.get("/finance/statements").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const gen = async () => {
    if (!cust || !from || !to) return toast.error("Pick customer + period");
    try {
      await api.post("/finance/statements", { customer_account_id: cust.id, period_start: from, period_end: to });
      toast.success("Statement generated"); load();
    } catch (e) { toast.error(formatError(e, "Failed")); }
  };
  return (
    <div>
      <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-4">Statements</h1>
      <div className="bg-white border border-slate-200 rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <div className="md:col-span-2"><Label className="label-overline">Customer</Label>
          <CustomerPicker value={cust} onChange={setCust} testId="stmt-customer" /></div>
        <div><Label className="label-overline">From</Label>
          <Input data-testid="stmt-from" type="date" className="rounded h-10 mt-1 font-mono" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label className="label-overline">To</Label>
          <Input data-testid="stmt-to" type="date" className="rounded h-10 mt-1 font-mono" value={to} onChange={(e) => setTo(e.target.value)} /></div>
        <Button className="rounded bg-blue-700 hover:bg-blue-800" data-testid="stmt-gen" onClick={gen}>Generate</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded overflow-x-auto">
        <table className="tbl w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50"><tr className="label-overline text-slate-600">
            <th className="text-left px-4 py-2">#</th><th className="text-left px-4 py-2">Customer</th>
            <th className="text-left px-4 py-2">Period</th>
            <th className="text-right px-4 py-2">Opening</th><th className="text-right px-4 py-2">Closing</th><th></th>
          </tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-slate-100">
                <td className="px-4 py-2 font-mono">{s.statement_no}</td>
                <td className="px-4 py-2">{s.customer_snapshot?.display_name}</td>
                <td className="px-4 py-2 font-mono text-xs">{s.period_start} → {s.period_end}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(s.opening_balance_cents)}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(s.closing_balance_cents)}</td>
                <td className="px-4 py-2"><button className="text-blue-700 font-semibold" onClick={() => dl(`/finance/statements/${s.id}/pdf`)}>PDF ↓</button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No statements yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Branding() {
  const uploadRef = React.useRef(null);
  const [kind, setKind] = useState("logo");
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const res = await fetch(`${API_BASE}/finance/branding/${kind}`, {
        method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd,
      });
      if (!res.ok) throw { response: { data: await res.json() } };
      toast.success(`${kind} uploaded`);
    } catch (err) { toast.error(formatError(err, "Upload failed")); }
    finally { if (uploadRef.current) uploadRef.current.value = ""; }
  };
  return (
    <div className="max-w-2xl">
      <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">Branding</h1>
      <p className="text-sm text-slate-600 mb-4">Upload your logo and letterhead. These appear on invoices, credit notes, and statements.</p>
      <div className="bg-white border border-slate-200 rounded p-4">
        <div className="flex gap-2">
          <select className="h-10 border border-slate-300 rounded px-2 font-mono" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="logo">Logo</option><option value="letterhead">Letterhead</option>
          </select>
          <input ref={uploadRef} type="file" accept="image/*" data-testid="brand-input" onChange={upload}
                 className="border border-slate-300 rounded px-2 h-10" />
        </div>
        <p className="text-xs text-slate-500 mt-3">Also configure business name, VAT, banking, notes and terms in Settings.</p>
      </div>
    </div>
  );
}
